import { computed, effect, EffectRef, inject, Injectable, isDevMode, Signal, signal, untracked, WritableSignal } from '@angular/core';
import { MapViewerInitOptions, MapViewerService, Viewer3dInitOptions, Viewer3dService} from '@3ddv/ngx-dvm-internal';
import { MapViewerLoadMapOptions, Viewer3dLoadView3dOptions, MapViewerFlagsAPI, MapViewerLoadObject, Viewer3dLoadObject, Viewer3dFlagsAPI, MapViewerPlugins, Viewer3dPlugins, MapViewerStylesLevel, MapViewerStylesState, MapViewerNode } from '@3ddv/dvm-internal';
import { AppViews } from '../models/enums/dvm/DvmViews.enum';
import { Configuration } from '../models/interfaces/configuration/index.interface';
import { APP_CONFIG } from '../configuration/configuration';
import { AppNodes } from '../models/enums/dvm/DvmNodes.enum';
import { catchError, Observable, tap } from 'rxjs';
import { NavigationService } from './navigation.service';
import { MINIMAP, VIEWER, VIEWER3D } from '@dvm.providers';
import { ViewerStylesOptions } from '@models/interfaces/dvm/ViewerStylesOptions.interface';
import { ViewService } from './view.service';
import { FilterService } from './filter.service';
import { Filter } from '@models/types/app/Filter.type';
import { SeatmapDisplay } from '@models/types/components/SeatmapDisplay.type';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';
import { UiService } from './ui.service';
import { GeneralAdmission } from '@models/types/components/GeneralAdmission.type';
import { DvmTemplates } from '@models/enums/dvm/DvmTemplates.enum';
import { TemplateService } from './template.service';
import { CustomAvailability, RandomAvailability } from '@models/types/components/Availability.type';
import { ToastService } from './toast.service';
import { ConfigurationService } from './configuration.service';


@Injectable({
  providedIn: 'root'
})
export class DvmService {

  // CONFIGURATION
  private readonly config:                Signal<Configuration>            = inject(APP_CONFIG);
  private readonly dvmConfig:             Signal<Configuration['Dvm']>     = computed((): Configuration['Dvm'] => this.config().Dvm);
  private readonly generalConfig:         Signal<Configuration['General']> = computed((): Configuration['General'] => this.config().General);
  private readonly uiconfig:              Signal<UiConfiguration>          = inject(UiService).uiConfig;


  // COMPUTED CONFIGURATION
  private readonly seatmapDisplay:        Signal<SeatmapDisplay>           = computed((): SeatmapDisplay => this.uiconfig().SeatmapDisplay);
  private readonly gaExceptions:          Signal<GeneralAdmission>         = computed((): GeneralAdmission => this.uiconfig().GeneralAdmission);
  private readonly view3dDisplayEnabled:  Signal<boolean>                  = computed((): boolean => this.uiconfig().View3dDisplay.enabled);
  private readonly seatLevelEnabled:      Signal<boolean>                  = computed((): boolean => this.uiconfig().SeatLevelDisplay.enabled);
  private readonly customAvailability:    Signal<CustomAvailability>       = computed((): CustomAvailability => this.uiconfig().CustomAvailability);
  private readonly randomAvailability:    Signal<RandomAvailability>       = computed((): RandomAvailability => this.uiconfig().RandomAvailability);


  // SERVICES
  private readonly navigation:            NavigationService                = inject(NavigationService);
  private readonly viewService:           ViewService                      = inject(ViewService);
  private readonly filters:               FilterService                    = inject(FilterService);
  private readonly template:              TemplateService                  = inject(TemplateService);
  private readonly toast:                 ToastService                     = inject(ToastService);
  private readonly configuration:         ConfigurationService             = inject(ConfigurationService);


  // DVM SERVICES
  public readonly viewer:                 MapViewerService                 = inject(VIEWER);
  public readonly minimap:                MapViewerService                 = inject(MINIMAP);
  public readonly viewer3d:               Viewer3dService                  = inject(VIEWER3D);


  // VIEWERS INIT CONFIGURATION
  private viewerInitOptions:               MapViewerInitOptions            = {};
  private viewer3dInitOptions:             Viewer3dInitOptions             = {};
  private minimapInitOptions:              MapViewerInitOptions            = {};


  // VIEWERS LOAD CONFIGURATION
  private viewerLoadOptions:               MapViewerLoadMapOptions         = {venue_id: '', map_id: ''};
  private minimapLoadOptions:              MapViewerLoadMapOptions         = {venue_id: '', map_id: ''};
  private viewer3dLoadOptions:             Viewer3dLoadView3dOptions       = {venue_id: '', view_id:''};


  // VIEWER STYLES
  private viewerStyles!:                   MapViewerStylesLevel[] | undefined;

  // VIEWER AVAILABILITY
  private availability!:                   string[];
  private gaSections!:                     string[];

  // STATE
  public  readonly is3d:                   WritableSignal<boolean>         = signal<boolean>(this.dvmConfig().viewer.is3d ?? false);
  public  readonly view:                   Signal<AppViews|null>           = this.viewService.view;
  private readonly importMode:             Signal<boolean>                 = this.configuration.isImportPreviewMode;
  private readonly duration:               number                          = 300;


  // EFFECTS
  public readonly configChange:            EffectRef                       = effect((): void => {

    const config:   Configuration['Dvm']['viewer'] = this.dvmConfig().viewer, // Conseguimos la configuración del DVM.
          current:  MapViewerLoadMapOptions        = this.viewerLoadOptions,  // Conseguimos la configuración del mapa actual,
          importMode: boolean                      = untracked(this.importMode)

    untracked((): void => {

      // VIEWERS: Si los viewers no están inicializados o la configuración es nula, no hacemos nada.
      if( (!this.viewer.isInitialized() || !this.viewer3d.isInitialized()) || !config ) return;


      // CONFIG: Si la configuración del DVM ha cambiado, actualizamos los viewers.
      if( (config.venueId !== current.venue_id) || (config.mapId !== current.map_id ) ) {
        // Si la vista es 3D, cerramos el viewer 3D.
        if(this.is3d()) this.close3dView();
  
        if(!importMode) this.initViewers();
        
        return;

      }

      // Skip 2D map-dependent operations when no map is configured (e.g. 3D-only sites)
      if (!this.viewerLoadOptions.map_id) return;

      //SEAT LEVEL: Check if seatLevel has changes and set layers viewer
      const seatLevel: boolean = this.seatLevelEnabled();
      if (seatLevel !== null) this.setLayers(!seatLevel, seatLevel ? 1 : 0);
      

      //AVAILABILITY: check if custom availaibility or random availability has changed
      const {enabled, data}          = this.customAvailability(),
            {enabled: randomEnabled} = this.randomAvailability();
    
      if (enabled && data) this.handleCustomAvailaibilityData();
      if (randomEnabled)   this.handleRandomAvailablity();
      

      // MINIMAP: Comprobamos si el minimapa ha cambiado.
      const minimapConfig:  Configuration['Dvm']['minimap'] = this.dvmConfig().minimap,
            minimapCurrent: MapViewerLoadMapOptions         = this.minimapLoadOptions;
  
      // Si el minimapa no está inicializado o la configuración del minimapa ha cambiado, lo inicializamos.
      if( minimapConfig.enabled && (!this.minimap.isInitialized() && minimapConfig.enabled) || minimapConfig.enabled && (minimapConfig.mapId !== minimapCurrent.map_id)) {
  
        this.initMinimap();
  
      }
      
      // STYLES: Update styles 
      if (config.styles && this.viewer.isInitialized()) {
        
        // Limpiamos estilos
        this.clearStyles();
  
        // Si es "default" o "new", llamamos a recargar el mapa para traer los estilos default
        if(Object.values(DvmTemplates).includes( config.styles.selected! as DvmTemplates)) {
          this.loadMap().subscribe();
          return;
        }
  
        // Si no, pasamos la nueva template seleccionada a viewerStyles y aplicamos cambios sin recargar mapa
        this.setViewerInitStyles(config.styles);
        this.setViewerStyles(this.viewerStyles!);
      
      }

    })

  }, {allowSignalWrites: true});


  // GETTERS
  public  get currentVenue():  string | null {
    return ( this.viewer.isInitialized() && this.viewer.getVenueId() ) ?
      this.viewer.getVenueId()! :
      null;
  }

  public  get currentMap():    string | null {
    return ( this.viewer.isInitialized() && this.viewer.getMapId() ) ?
      this.viewer.getMapId()! :
      null;
  }

  public  get currentView3d(): string | null {
    return ( this.viewer3d.isInitialized() && this.viewer3d.getViewId() ) ?
      this.viewer3d.getViewId()! :
      null;
  }

  private get isMinimapEnabled(): boolean {
    return this.dvmConfig().minimap.enabled && this.minimap.isInitialized();
  }


  // MAIN INIT METHOD
  public initViewers(): void {

    this.initMapViewer(); // Inicia el viewer 2D

    this.initViewer3d();  // Inicia el viewer 3D

  }

  //#region MAP VIEWER METHODS

  // PUBLIC METHODS
  public setAvailability(type?: string, availability?: string[], viewer: MapViewerService = this.viewer): void {

    // Si hay filtros, obtenemos los filtros por vista, si no devolvemos undefined.
    const filteredAvailability: string[] | undefined = this.filters.getFiltersByView(this.view()!).length ? this.filters.getFiltersByView(this.view()!) : undefined;

    // Si hay type y availabilty explícitos, los seteamos directamente.
    if(type && availability){

      viewer.setAvailability(type, availability)

    }
    // Si no viene ningun param
    else {

      // Iterate over each node type on the map. For each type, use the specific filter if one exists,
      // otherwise set all nodes of that type as available.
      const sectionFilter: string[] = this.filters.getFilterByKey(AppNodes.Section as keyof Filter);
      viewer.getTypesList().forEach((t) => {
        const filterKeys:     AppNodes[] = [AppNodes.Section, AppNodes.Row, AppNodes.Seat];
        const isStandardType: boolean    = filterKeys.includes(t as AppNodes);
        const typeFilter                 = isStandardType ? this.filters.getFilterByKey(t as keyof Filter) : [];

        // For non-standard types (area, areaprice, etc.) with an active section filter,
        // only keep available the section filter IDs that belong to that type.
        // GA is excluded from this logic and always keeps all its nodes available.
        // The viewer === this.viewer check ensures this logic does not affect the minimap,
        // which shares the same FilterService but should not be restricted by the menu filter.
        if (!isStandardType && t !== AppNodes.Ga && sectionFilter.length && viewer === this.viewer) {
          const idsOfType: string[] = viewer.getNodesByType(t).map(n => n.id!);
          viewer.setAvailability(t, sectionFilter.filter(id => idsOfType.includes(id)));
        } else {
          const resolved = availability ?? (typeFilter.length ? typeFilter : viewer.getNodesByType(type ?? t));
          viewer.setAvailability(type ?? t, resolved);
        }
      });

    }

    // Finalmente, si habia filtros, seleccionamos los nodos establecidos en los filtros.
    if(filteredAvailability && this.view() !== AppViews.Seatmap ) this.viewer.select(filteredAvailability);

  }

  public loadMap(mapId?: string, type?: string, availability?: string[]): Observable<MapViewerLoadObject> {

    const options: MapViewerLoadMapOptions = mapId ? Object.assign({}, this.viewerLoadOptions, {map_id: mapId}) : this.viewerLoadOptions;

    if(this.importMode() && !this.viewerStyles) this.setViewerInitStyles(this.dvmConfig().viewer.styles);

    return this.viewer.loadMap(options).pipe(
      tap((): void => {
        // Seteamos la vista actual
        this.setCurrentView();

        // Seteamos disponibilidad
        this.setAvailability(type, availability);

        // Seteamos los flags del viewer
        this.setViewerFlags(this.dvmConfig().misc.flags?.viewerFlags);

        // Registramos la navegación
        this.registerNavigation(options.map_id);

        // Si hay estilos, los seteamos
        if(this.viewerStyles) this.setViewerStyles(this.viewerStyles);

        // Si el minimapa está habilitado, vamos a la sección del mapa informada
        if(this.isMinimapEnabled) this.minimapGoTo(options.map_id!);

        //if bestNodes enabled, pre-select best nodes
        if(this.seatmapDisplay().enabled && this.seatmapDisplay().bestNode && this.view() === AppViews.Seatmap) this.selectBestNodes();

        //if customAvailability enabled, set given nodes as unavailable
        if(this.customAvailability().enabled) this.handleCustomAvailaibilityData();

        //if randomAvailability enabled, ste availability per node type
        if(this.randomAvailability().enabled) this.handleRandomAvailablity();
      }),
      catchError((error: unknown) => { throw error })
    )

  }

  public clearStyles(): void {
    this.viewerStyles = undefined;
  }

  public resetViewer(loadMap: boolean = true): void {

    // Reseteamos el historial de navegación
    this.navigation.reset();

    // Si la vista es 3D, cerramos el viewer 3D.
    if(this.view() === AppViews.Pano) this.close3dView();

    if((this.currentMap !== this.viewerLoadOptions.map_id) && loadMap) this.loadMap().subscribe(); // Si el mapa no es el mismo que el inicial, lo cargamos de nuevo.

  }


  // PRIVATE METHODS
  private initMapViewer(): void {

    // Si no está inicializado el viewer, lo inicializamos.
    if(!this.viewer.isInitialized()) {

      this.setViewerInitialConfig();

      this.viewer.initialize(this.viewerInitOptions).subscribe();

    }

    // Esperamos que encienda y cargamos las load options, el debugger, el mapa y el minimapa
    this.viewer.waitInitialize().subscribe((): void => {

      this.setViewerLoadOptions(this.dvmConfig().viewer);

      this.addDvmDebugger();

      if (!this.viewerLoadOptions.venue_id || !this.viewerLoadOptions.map_id) return;

      this.loadMap().subscribe((): void => {

        if(this.dvmConfig().minimap.enabled) this.initMinimap(); // Cargamos el mapa y si el minimapa está habilitado, lo iniciamos.

        if(this.seatLevelEnabled()) this.setLayers(false, 1) //Set layers according to seatLevelDisplay
      });

    })

  }

  private setViewerInitialConfig(): void {

    const config: Configuration['Dvm'] = this.dvmConfig();
    this.viewerInitOptions.styles_by_groups = true;

    this.setViewersPlugins(config.misc);

    if(config.viewer.styles){
      this.setViewerInitStyles(config.viewer.styles);
    }

  }

  private setViewerLoadOptions(config: Configuration['Dvm']['viewer'], type: 'viewer' |  'viewer3d' | 'minimap' = 'viewer'): void{

    switch(type){

      case 'viewer':

        this.viewerLoadOptions.venue_id   = config.venueId;
        this.viewerLoadOptions.map_id     = config.mapId;

      break;

      case 'viewer3d' :

        this.viewer3dLoadOptions.venue_id = config.venueId;

      break;

      case 'minimap':

        this.minimapLoadOptions.venue_id = config.venueId;
        this.minimapLoadOptions.map_id   = this.dvmConfig()['minimap'].mapId;

      break;

    }

  }

  private setViewersPlugins(config: Configuration['Dvm']['misc']): void {

    const plugins = config.plugins;

    // MAP VIEWER PLUGINS
    this.viewerInitOptions.plugins   = plugins.viewerPlugins?.length	? plugins.viewerPlugins : this.setDefaultPlugins();

    // VIEWER 3D PLUGINS
    this.viewer3dInitOptions.plugins = plugins.viewer3dPlugins?.length ? plugins.viewer3dPlugins : this.setDefault3dPlugins();

  }

  private setDefaultPlugins(): (keyof MapViewerPlugins)[] {

    const plugins: (keyof MapViewerPlugins)[] = [];

    plugins.push(
      'navigable_minimap',
      'best_nodes'
    );

    return plugins;

  }

  private setViewerFlags(flags?: MapViewerFlagsAPI): void {

    if(!flags) return this.setDefaultFlags();

    Object.entries(flags).forEach(([key, value]): void => {

      if(Object.prototype.hasOwnProperty.call(this.viewer.flags, key))  this.viewer.flags[key as keyof MapViewerFlagsAPI] = value;

    });

  }

  private setDefaultFlags(): void {
    this.viewer.flags.fixed_aspect_ratio      = false;
    this.viewer.flags.automatic_selection     = false;
  }

  private setViewerStyles(styles?: MapViewerStylesLevel[]): void {
    // debugger;
    if(!styles) return;

    const currentStyles: MapViewerStylesLevel[] = this.viewer.getStyles();
    
    styles.forEach((style: MapViewerStylesLevel, index: number): void => {
      
      const keys: string[] = Object.keys(currentStyles[index]);

      if(!Object.keys(style).some(key => keys.includes(key))) return;

      keys.forEach((key: string) => {

        if(key in style) currentStyles[index][key] = style[key];

      })
      
    });

    this.viewer.setStyles(currentStyles, true);

  }

  private setViewerInitStyles(styles: ViewerStylesOptions): void {

    const styleKey: ViewerStylesOptions['selected']     = styles.selected ?? 'default',
          template: MapViewerStylesLevel[] | undefined  = this.template.selectedTemplate()?.template,
          fallback: MapViewerStylesLevel[] | undefined  = (styles && styles.templates ? styles.templates![styleKey!] : undefined) ?? undefined;

    if(!template && !fallback) return;

    this.viewerStyles = template ?? fallback!;

  }

  private selectBestNodes(): void {

    this.viewer.best_nodes?.getBestNodes({ type: AppNodes.Seat, quantity: 1 }).subscribe({

      next: (nodes): void => {

        if (!nodes || nodes.length === 0) return;

        this.viewer.select(nodes)

        setTimeout(() => this.viewer.goTo(nodes[0].id!), this.duration + 250);

      },
      error: (err) => console.error('Error getting best nodes', err)
    });

  }

  private handleCustomAvailaibilityData() {
    const data = this.customAvailability().data;
    if (!data) return;

    const appNodes = this.viewer.getTypesList();
    appNodes.forEach((nodeType) => {
      data[nodeType as AppNodes]?.forEach((id) => {
        this.viewer.setUnavailable(nodeType, id);
      });
    });
  }

  private handleRandomAvailablity() {
    const view = this.view();
    const random = this.randomAvailability();
  
    if (!random.enabled || !random.applyTo.length) return;

    random.applyTo.forEach((node) => {
      const percentage =
        node === AppNodes.Ga
          ? random.percentages[AppNodes.Section]! / 100
          : random.percentages[node!]! / 100;

      if (
        (view === AppViews.Topview && (node === AppNodes.Section || node === AppNodes.Ga)) ||
        (view === AppViews.Seatmap && node === AppNodes.Seat)
      ) {
        this.viewer.setAvailability(node, this.getRandomAvailability(node, percentage));        
      }
    });
    
  }

  private getRandomAvailability(type: string, availabilityRatio: number): MapViewerNode[] {
    return this.viewer.getNodesByType(type).filter((node) => {
        if (node.parent) {
            const parent = this.viewer.getNodeById(node.parent);
            if (parent && parent.state === "available" || parent && parent.state === "selected") {
                return Math.random() < availabilityRatio;
            } else {
                return false;
            }
        } else {
            return Math.random() < availabilityRatio;
        }
    });
  }


  //#endregion


  //#region VIEWER 3D METHODS

  // PUBLIC METHODS

  public load3dView(viewId: string): Observable<Viewer3dLoadObject | Error> {

    const viewVersion: string = this.uiconfig().View3dDisplay.viewVersion;
    const options: Viewer3dLoadView3dOptions = Object.assign({}, this.viewer3dLoadOptions, {view_id: viewId});
    if (viewVersion) options.set_id = viewVersion;

    this.toggle3dView(true);

    return this.viewer3d.loadView3d(options).pipe(
      tap((): void => {
        this.setCurrentView();
        if(this.isMinimapEnabled && this.apply3DView(viewId)) this.minimapGoTo(viewId);
      }),
      catchError((): Observable<Error> => {

        const errorMessge: string = 'Error loading 3D view';

        // Si hay error al cargar pano, forzamos 3d view a false y reseteamos view signal y viewer
        this.toggle3dView(false);
        this.setCurrentView();
        this.resetViewer();

        // Avisamos al usuario
        this.toast.show(errorMessge, 'error', 2000);
        throw new Error(errorMessge);

      })
    );

  }

  public close3dView(): void {

    this.toggle3dView(false);

    this.viewer3d.reset();

    this.setCurrentView();

  }

  // PRIVATE METHODS
  private initViewer3d(): void {

    const config = this.dvmConfig().viewer;

    this.setViewerLoadOptions(config, 'viewer3d');

    if(!this.viewer3d.isInitialized()) {

      this.viewer3d.initialize(this.viewer3dInitOptions).subscribe((): void => {

        this.setViewer3dFlags();

        if (config.is3d && config.viewId) {
          this.load3dView(config.viewId).subscribe();
        }

      });

    } else if (config.is3d && config.viewId) {

      this.load3dView(config.viewId).subscribe();

    }

  }

  private setViewer3dFlags(flags?: Viewer3dFlagsAPI): void {

    // Default flags
    if(!flags) return this.setDefault3dFlags();

    // Custom flags
    Object.entries(flags).forEach(([key, value]) => {

      if(Object.prototype.hasOwnProperty.call(this.viewer3d.flags, key)){

        this.viewer3d.flags[key as keyof Viewer3dFlagsAPI] = value;

      }

    });

  }

  private setDefault3dFlags(): void {

    this.viewer3d.flags.fixed_aspect_ratio     = false;
    this.viewer3d.flags.show_fullscreen_button = false;

    // RELATED PLUGIN FLAGS
    if(this.viewer3d.related_previews){
      this.viewer3d.related_previews.flags.enable_native_interface = false;
      this.viewer3d.related_previews.flags.automatic_track         = true;
      this.viewer3d.related_previews.flags.enabled                 = true;
    }

  }

  private setDefault3dPlugins(): (keyof Viewer3dPlugins)[] {

    const plugins: (keyof Viewer3dPlugins)[] = [];

    plugins.push(
      // ...
    );

    return plugins;

  }

  private toggle3dView(show: boolean): void {

    if(show !== this.is3d()) {

      this.is3d.update(():boolean => show);

    }

  }


  //#endregion


  //#region MINIMAP METHODS

  private initMinimap(): void {

    // Si el mapa es tipo main, cargamos minimapa del plugin navigable_minimap.
    if(this.view() === AppViews.Main) return;

    // Si no, cargamos minimapa custom
    if(!this.minimap.isInitialized()) {


      this.minimap.initialize(this.minimapInitOptions).subscribe((): void => {

        this.setViewerLoadOptions(this.dvmConfig().viewer, 'minimap');

        this.minimap.loadMap(this.minimapLoadOptions).subscribe((): void => {

          this.setMinimapFlags();

          this.setAvailability(undefined, undefined, this.minimap);

          this.setMinimapStyles();

          if(this.currentMap) this.minimapGoTo(this.currentMap);

        });

      });

    }
    else{

        this.setViewerLoadOptions(this.dvmConfig().viewer, 'minimap');

        this.minimap.loadMap(this.minimapLoadOptions).subscribe((): void => {

          this.setMinimapFlags();

          this.setAvailability(undefined, undefined, this.minimap);

          if(this.currentMap) this.minimapGoTo(this.currentMap);

        });

    }

  }

  private minimapGoTo(mapId: string): void {

    if(this.viewerLoadOptions.map_id !== mapId) {

      this.minimap.unselectAll();

      this.minimap.select(mapId);

      this.minimap.goTo(mapId);

    }
    else{

      this.minimap.unselectAll();

      if(this.minimap.limits?.bbox_limits){
        this.minimap.goTo(this.minimap.limits.bbox_limits);
      }

    }

  }

  private setMinimapFlags(flags?: MapViewerFlagsAPI): void {

    if(!flags){
      this.setMinimapDefaultFlags();
      return;
    }

    Object.entries(flags).forEach(([key, value]) => {
      if(Object.prototype.hasOwnProperty.call(this.minimap.flags, key)){
          this.minimap.flags[key as keyof MapViewerFlagsAPI] = value;
      }
    });

  }

  private setMinimapDefaultFlags(): void {

    const minimap = this.minimap;

    minimap.flags.fixed_aspect_ratio = false;
    minimap.max_selection = 1;

  }

  private setMinimapStyles(): void {

    const minimapStyles:    MapViewerStylesLevel = this.minimap.getStyles()[0],
          selectedSection:  MapViewerStylesState = minimapStyles['section']['selected'] as MapViewerStylesState;

    selectedSection.normal.none.fillStyle = '#fff';

    this.minimap.setStyles([minimapStyles], true);

  }

  //#endregion MINIMAP METHODS


  //#region DVM UTILS

  private registerNavigation(mapId: string | undefined): void {

    if(!mapId) return; // Si no hay mapId, no hacemos nada.

    // Añadimos la vista a la historia de navegación.
    this.navigation.next({
      map_id: mapId,
      view:   this.view()!
    });

  }

  /**
   * Método que establece la current view. Se llama al cambiar de mapa.
   * El método consigue los tipos de nodo filtrando los de General Admission y
   * llama a determineView para determinar la vista actual. Una vez determinada,
   * se setea en el currentView.
   *
   * @returns void
   */
  private setCurrentView(): void {

    // Si es 3D, no se comprueban los nodos y se setea la vista a Pano.
    if(this.is3d()) {
      this.viewService.setView(AppViews.Pano);
      return;
    }

    const nodeTypes:    string[]   = this.viewer.getTypesList().filter((type: string) => type !== AppNodes.Ga), // Conseguimos todos los nodos filtrando los GA.
          currentView:  AppViews   = this.determineView(nodeTypes);                                             // Determinamos la vista actual.

    this.viewService.setView(currentView);

  }

  /**
   * Método que determina la view en base a los nodos o el tipo de layer que exista en el mapa.
   * Las comprobaciones que se hacen son las siguientes:
   * 1. Como los nodes llegan filtrando General Admission, si solo hay un nodo, la vista es Topview si es Section, Seatmap si es cualquier otro.
   * 2. Si hay nodos que contienen _nh, la vista es Seatmap.
   * 3. Si no solo llega un nodo y no se contiene ningún nodo nh, se comprueba que exista la layer mainlayer,
   * si existe, la vista es topview, si no, main. (Esto es un poco confuso, pero los mapas main no incluyen mainlayer en sus layers)
   *
   * @param nodes
   * @returns
   */

  private determineView(nodes: string[]): AppViews {

    if (nodes.length === 1) {
      return nodes[0] === AppNodes.Section ? AppViews.Topview : AppViews.Seatmap;
    }

    if (nodes.some(node => /_nh/.test(node))) {
      return AppViews.Seatmap;
    }

    if(this.viewer.layers.getLayers().includes('mainlayer')) {
      return AppViews.Topview;
    }

    if(this.viewer.layers.getLayerLevels() === 2) {
      return AppViews.Main;
    }
    //changes fallback to seatmap view when there is no mainlayer and only one layer level, needs testing
    return AppViews.Seatmap;

  }

    //Set seat level view when ui is changed
  public setLayers(automaticControlLevel: boolean, level: number): void {
    this.viewer.layers.flags.automatic_control_level = automaticControlLevel;
    this.viewer.layers.setLayerLevel(level);
  }

  /**
   * Añade el DVM a la window para poder debuggear el viewer.
   * @returns
   */
  private addDvmDebugger(): void {

    if(!isDevMode()) return;

    (window as { dvm?: MapViewerService }).dvm                        = this.viewer;
    (window as { dvm3d?: Viewer3dService }).dvm3d                     = this.viewer3d;
    (window as { dvmMinimap?: MapViewerService }).dvmMinimap          = this.minimap;
    (window as { currentView?: Signal<AppViews | null> }).currentView = this.view;

  }

  //#endregion

  //#region Seatmap + 3D conditions
  public apply3DView(nodeId: string): boolean {
    return this.view3dDisplayEnabled() &&
      (this.isGAException(nodeId) || !this.seatmapDisplay().enabled);
  }

  public isGAException(nodeId: string): boolean {
    const ga = this.gaExceptions();
    return ga.enabled && ga.data.includes(nodeId);
  }

  public selectNodeInTopview(nodeId: string) {
    return !this.view3dDisplayEnabled() && this.isGAException(nodeId) || (!this.seatmapDisplay().enabled && !this.view3dDisplayEnabled());
  }
  //#endregion

}

